// ============================================================
// TATHYA DMS - Login Screen
// Version: 1.0.2
// Date: 2026-04-18
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { login } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import TCLogo from './TCLogo';

const Login = () => {
  const { org_slug } = useParams();
  const navigate = useNavigate();
  const { loginUser } = useAuth();

  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await login({
        email: form.email,
        password: form.password,
        org_slug: org_slug || null
      });
      const { accessToken, refreshToken, user } = res.data;
      loginUser(user, { slug: org_slug }, accessToken, refreshToken);
      if (user.password_reset_required) {
        navigate(`/${org_slug}/change-password`);
      } else if (user.role === 'platform_admin') {
        navigate('/admin/dashboard');
      } else {
        navigate(`/${org_slug}/dashboard`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>

        {/* Header */}
        <div style={styles.header}>
          <TCLogo size={64} />
          <h1 style={styles.title}>CONSAM DMS</h1>
          <p style={styles.subtitle}>Data Management System</p>
          {org_slug && (
            <div style={styles.orgBadge}>{org_slug.toUpperCase()}</div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email Address</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({...form, email: e.target.value})}
              placeholder="Enter your email"
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={form.password}
              onChange={e => setForm({...form, password: e.target.value})}
              placeholder="Enter your password"
              required
            />
          </div>
          {error && <p className="error-msg">{error}</p>}
          <button
            type="submit"
            className="btn btn-primary"
            style={styles.submitBtn}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        {/* Forgot password */}
        <div style={styles.footer}>
          <a href={`/${org_slug}/forgot-password`} style={styles.link}>
            Forgot your password?
          </a>
        </div>
      </div>

      {/* Copyright */}
      <p style={styles.copyright}>
        &copy; 2026 CONSAM. All rights reserved.
      </p>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #1B3A5C 0%, #2E75B6 100%)',
    padding: '20px'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px'
  },
  title: {
    fontSize: '26px',
    fontWeight: '700',
    color: '#1B3A5C',
    marginTop: '12px',
    marginBottom: '4px',
    letterSpacing: '0.5px'
  },
  subtitle: {
    color: '#595959',
    fontSize: '13px',
    marginBottom: '12px'
  },
  orgBadge: {
    display: 'inline-block',
    background: '#D6E4F0',
    color: '#1B3A5C',
    padding: '4px 14px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '700',
    letterSpacing: '1.5px'
  },
  submitBtn: {
    width: '100%',
    padding: '12px',
    fontSize: '15px',
    marginTop: '8px'
  },
  footer: {
    textAlign: 'center',
    marginTop: '20px'
  },
  link: {
    color: '#2E75B6',
    textDecoration: 'none',
    fontSize: '13px'
  },
  poweredBy: {
    textAlign: 'center',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid #f0f0f0',
    fontSize: '12px',
    color: '#595959'
  },
  copyright: {
    color: 'rgba(255,255,255,0.5)',
    fontSize: '11px',
    marginTop: '20px',
    textAlign: 'center'
  }
};

export default Login;
